/*
 * Default off. Convert's variation sets hnWishlistRequested and calls enable().
 * No app scripts, styles, storage, API calls, or cart hooks run in the control.
 * Keep Magic Wishlist's global app embed disabled; its native blocks supply the
 * current asset URLs and customer configuration inside inert <template>s.
 */
(() => {
  if (window.HNWishlist) return;

  const root = document.documentElement;
  const loader = document.querySelector('[data-hn-wishlist-loader]');
  let activation;
  const loadedScripts = new Set();

  const loadScript = (src) => {
    if (loadedScripts.has(src)) return Promise.resolve();
    loadedScripts.add(src);
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Wishlist script failed to load'));
      document.head.append(script);
    });
  };

  const loadStylesheet = (href) => new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = resolve;
    link.onerror = () => reject(new Error('Wishlist styles failed to load'));
    document.head.append(link);
  });

  const activate = async () => {
    if (document.readyState === 'loading') {
      await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
    }
    const templates = [...document.querySelectorAll('template[data-hn-wishlist-template]')];
    if (!loader || !templates.length) return;
    root.dataset.hnWishlist = 'loading';

    const scripts = [];
    const stylesheets = new Set();
    for (const template of templates) {
      // importNode keeps template scripts inert. Recreate them explicitly below,
      // after every native block is mounted, just as deferred app scripts expect.
      const fragment = document.importNode(template.content, true);
      fragment.querySelectorAll('script').forEach((script) => {
        scripts.push(script);
        script.remove();
      });
      fragment.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
        stylesheets.add(link.href);
        link.remove();
      });
      [...fragment.children].forEach((element) => element.setAttribute('data-hn-wishlist-mounted', ''));
      template.replaceWith(fragment);
    }

    // Derive the heart asset URLs from the native header block so an app release
    // cannot leave the theme pinned to an old extension version.
    const pageScript = scripts.find((script) => /\/wishlist-page\.js(?:\?|$)/.test(script.src));
    if (!pageScript) throw new Error('Native wishlist header assets are missing');
    const heartScriptUrl = new URL('wishlist-heart.js', pageScript.src).href;
    stylesheets.add(new URL('wishlist-heart.css', pageScript.src).href);
    // Insert theme overrides last to preserve the HN styles.
    await Promise.all([...stylesheets].map(loadStylesheet));
    await loadStylesheet(loader.dataset.stylesUrl);

    // These are the native Liquid-rendered app configuration scripts, including
    // current customer and product/variant context. Never evaluate remote HTML.
    scripts.filter((script) => !script.src).forEach((original) => {
      const script = document.createElement('script');
      for (const attribute of original.attributes) script.setAttribute(attribute.name, attribute.value);
      script.textContent = original.textContent;
      document.head.append(script);
    });

    window.wishlistHeartConfig = {
      ...window.wishlistHeartConfig,
      addToWishlistLabel: 'Add to Wishlist',
      addedToWishlistLabel: 'Added to Wishlist',
      removeFromWishlistLabel: 'Remove from Wishlist',
      position: 'top-right',
      iconColor: '#162f28',
      activeColor: '#71975c',
      showCount: false,
      skipProductHandle: loader.dataset.productHandle,
      customSelectors: { productCard: '[data-hn-wishlist-card]:not([data-wishlist-off])' },
    };
    // The header block omits these embed-only options. Preserve the approved
    // test behavior without enabling the global embed or its login nudges.
    window.wishlistConfig.features = {
      ...window.wishlistConfig.features,
      autoAddWishlist: false,
      autoRemoveOnCart: false,
      requireLoginToSave: false,
      showVariantPicker: false,
    };
    window.wishlistCollectionVariants = window.wishlistCollectionVariants || {};

    await loadScript(loader.dataset.enhancementsUrl);
    for (const script of scripts.filter((script) => script.src)) {
      await loadScript(script.src);
    }
    await loadScript(heartScriptUrl);
    root.dataset.hnWishlist = 'enabled';
    document.dispatchEvent(new CustomEvent('hn:wishlist:ready'));
  };

  window.HNWishlist = {
    enable() {
      // Both an early Convert assignment and a later activation are supported.
      // Repeated execution does not bind handlers or load native assets twice.
      window.hnWishlistRequested = true;
      if (!activation) {
        activation = activate().catch((error) => {
          root.dataset.hnWishlist = 'error';
          console.warn('[HN Wishlist] Activation failed; wishlist remains hidden.', error);
        });
      }
      return activation;
    },
  };

  if (window.hnWishlistRequested === true) window.HNWishlist.enable();
})();
